// https://cloud.google.com/recaptcha/docs/instrument-web-pages
//
// The site key is a reCAPTCHA Enterprise key, so this loads enterprise.js and uses the
// grecaptcha.enterprise namespace. enterprise.js does NOT define the classic grecaptcha.execute,
// so the namespace is not interchangeable with api.js.
//
// The token is minted from validation/rulesetExternal.js as an external rule on the hidden field,
// the same way the Acquired card widget is wired. That means it is minted at submit time, after the
// rest of the form validates - which matters, because an Enterprise token expires after two minutes.

import $ from 'jquery'
import * as Sentry from '@sentry/browser'

const API_URL = 'https://www.google.com/recaptcha/enterprise.js?render='

// Marker attribute only - the minted token goes into the input's value, not into this attribute.
const SELECTOR_TOKEN = '[data-recaptcha-token]'
const ATTRIBUTE_ACTION = 'data-recaptcha-action'
const ATTRIBUTE_SITE_KEY = 'data-recaptcha-site-key'

// Past this there is nothing left to wait for: the caller submits with an empty token and lets the
// server answer with the translated, retry-able message rather than stranding the visitor. Long
// enough for a slow mobile connection to fetch and run the API, short enough to not feel broken.
const MINT_TIMEOUT = 5000 // ms

// Which step of the mint failed. Sentry levels and groups on it.
const STAGE = { load: 'load', timeout: 'timeout', execute: 'execute', unknown: 'unknown' }

// Steps the visitor's own network can cause are recorded, not paged on. execute() rejecting is ours
// - the API ran, so it points at the site key or the domain allow-list, and nothing else alerts.
const STAGE_LEVELS = {
    [STAGE.load]: 'warning',
    [STAGE.timeout]: 'warning',
    [STAGE.execute]: 'error',
    [STAGE.unknown]: 'error',
}

// Changing either re-groups every existing Sentry issue and orphans the old ones.
const FINGERPRINT_ROOT = 'recaptcha-mint-failure'
const TAG_STAGE = 'recaptcha_mint_stage'

// Google's rejection reaches Sentry through extraErrorDataIntegration, which does not truncate a
// non-Error cause.
const CAUSE_MAX_LENGTH = 200

let apiLoader = null
const reportedStages = new Set()

// Google and jQuery both reject with non-Errors; Sentry needs a real Error for a message and stack.
const mintFailure = (stage, message, cause) => Object.assign(new Error(message, { cause }), { stage })

const loadApi = (siteKey) => {
    if (apiLoader === null) {
        // The rejection must not be cached: an ad blocker or a blip on the first attempt would
        // otherwise make every later submit on this page fail instantly with an empty token.
        apiLoader = Promise.resolve($.getScript(API_URL + siteKey)).then(
            () => {
                // A filter answering with an empty body still fires the script's load event, so a
                // fetched script is no proof the API is there. Only ready() may be asserted: the real
                // enterprise.js defines execute() lazily, inside ready().
                if (typeof grecaptcha !== 'undefined' && typeof grecaptcha.enterprise?.ready === 'function') return

                apiLoader = null

                throw mintFailure(STAGE.load, 'reCAPTCHA API loaded without a usable grecaptcha.enterprise')
            },
            () => {
                apiLoader = null

                // jQuery fabricates status 404 for every script failure, so the jqXHR is dropped -
                // it would only mislead whoever opens the issue.
                throw mintFailure(STAGE.load, 'reCAPTCHA API could not be fetched')
            }
        )
    }

    return apiLoader
}

const apiReady = () => new Promise((resolve) => grecaptcha.enterprise.ready(resolve))

// Catches only its own rejections - loadApi is awaited before this runs, so a load failure keeps the
// step it was already tagged with instead of being relabelled here.
const mintToken = (siteKey, action) =>
    apiReady()
        .then(() => grecaptcha.enterprise.execute(siteKey, { action }))
        .catch((cause) => {
            throw mintFailure(STAGE.execute, 'reCAPTCHA token could not be minted', String(cause).slice(0, CAUSE_MAX_LENGTH))
        })

const rejectAfter = (delay) => {
    let timer = null

    const promise = new Promise((resolve, reject) => {
        timer = setTimeout(() => reject(mintFailure(STAGE.timeout, 'reCAPTCHA token minting timed out')), delay)
    })

    return { promise, clear: () => clearTimeout(timer) }
}

// An ad blocker or a network that cannot reach Google fails on every single submit, so report each
// step once per page rather than turning one visitor into a stream of identical Sentry events.
const reportMintFailure = (error) => {
    const stage = error?.stage ?? STAGE.unknown

    if (reportedStages.has(stage)) return

    reportedStages.add(stage)

    Sentry.captureException(error, {
        level: STAGE_LEVELS[stage],
        tags: { [TAG_STAGE]: stage },
        // One issue per known step, so a broken site key cannot hide inside a stream of blocked
        // scripts. An unrecognised failure keeps Sentry's own grouping instead.
        ...(stage !== STAGE.unknown && { fingerprint: [FINGERPRINT_ROOT, stage] }),
    })
}

/**
 * Mints a token for the given hidden field and writes it into the field's value.
 *
 * Never rejects. A visitor who cannot reach Google gets an empty token and the server's translated
 * "we could not confirm you are a real person" message; failing the validation rule instead would
 * attach an error to a hidden input, which renders nowhere and leaves a dead submit button.
 */
export const mintReCaptchaTokenInto = async ($input) => {
    const timeout = rejectAfter(MINT_TIMEOUT)

    try {
        const siteKey = $input.attr(ATTRIBUTE_SITE_KEY)

        if (!siteKey) return

        const mint = Promise.resolve(loadApi(siteKey)).then(() => mintToken(siteKey, $input.attr(ATTRIBUTE_ACTION)))

        $input.val(await Promise.race([mint, timeout.promise]))
    } catch (error) {
        reportMintFailure(error)
        $input.val('')
    } finally {
        timeout.clear()
    }
}

export default () => {
    const $tokens = $(SELECTOR_TOKEN)

    if (!$tokens.length) return

    const siteKey = $tokens.first().attr(ATTRIBUTE_SITE_KEY)

    if (!siteKey) return

    // Warm the API up while the visitor is still filling the form in, so the submit only pays for
    // execute(). A rejection here is handled on the minting path; swallow it so it does not surface
    // as an unhandled rejection.
    Promise.resolve(loadApi(siteKey)).catch(() => {})
}
